import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Boxes, CalendarPlus, Radio, ArrowRight, X, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SELLER_PATHS = [
  "/sell", "/my-shows", "/store-profile", "/seller-orders", "/returns",
  "/inventory", "/shipments", "/shipping-settings", "/simulcast", "/finances", "/insights",
];

const STEPS = [
  {
    icon: Store,
    title: "Welcome to your seller tools 🏪",
    body: "Everything you need to sell live is right here in the Seller Dashboard. Here's how to get your first show going in 3 quick steps.",
  },
  {
    icon: Boxes,
    title: "1. Add items to your inventory",
    body: "Open Inventory and create your products — add a photo, price, quantity and shipping. You'll pick from these when you build a show.",
  },
  {
    icon: CalendarPlus,
    title: "2. Create & schedule a show",
    body: "Hit Go Live to set up a show, give it a title and category, then drop in items as auctions, buy-it-now, or giveaways.",
  },
  {
    icon: Radio,
    title: "3. Go live & sell",
    body: "Start streaming and run your show in real time — buyers bid, buy and check out instantly. You can even simulcast to YouTube, Facebook & Twitch.",
  },
];

export function SellerOnboardingTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  const key = user?.user_id ? `ss_seller_onboard:${user.user_id}` : null;
  const onSellerPage = SELLER_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const welcomePending = user?.user_id && localStorage.getItem(`ss_onboard:${user.user_id}`) === "pending";

  useEffect(() => {
    if (key && onSellerPage && !welcomePending && localStorage.getItem(key) !== "done") {
      setOpen(true);
    }
  }, [key, onSellerPage, welcomePending]);

  const done = () => {
    if (key) localStorage.setItem(key, "done");
    setOpen(false);
  };
  const finish = () => { done(); navigate("/inventory"); };

  if (!open) return null;
  const step = STEPS[i];
  const Icon = step.icon;
  const last = i === STEPS.length - 1;

  return (
    <div className="ss-modal-back" style={{ zIndex: 60 }} data-testid="seller-onboarding-tour">
      <div className="ss-modal fade-up" style={{ maxWidth: 460, textAlign: "center", position: "relative" }} onClick={(e) => e.stopPropagation()}>
        <button className="disc-search" style={{ position: "absolute", top: 14, right: 14, width: 34, height: 34 }} onClick={done} data-testid="seller-onboarding-skip" title="Skip">
          <X size={18} />
        </button>

        <div style={{ width: 76, height: 76, borderRadius: 20, background: "var(--brand)", display: "grid", placeItems: "center", margin: "8px auto 18px", boxShadow: "0 10px 30px rgba(12,74,56,.35)" }}>
          <Icon size={34} style={{ color: "var(--gold)" }} />
        </div>

        <h2 style={{ fontSize: 23, fontWeight: 800 }} data-testid="seller-onboarding-title">{step.title}</h2>
        <p className="ss-muted" style={{ fontSize: 15, lineHeight: 1.7, marginTop: 10 }}>{step.body}</p>

        <div style={{ display: "flex", justifyContent: "center", gap: 7, margin: "22px 0" }}>
          {STEPS.map((_, n) => (
            <span key={n} onClick={() => setI(n)} style={{ width: n === i ? 22 : 8, height: 8, borderRadius: 999, background: n === i ? "var(--brand)" : "var(--line, #d9d4c7)", transition: "width .25s, background .25s", cursor: "pointer" }} />
          ))}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          {i > 0 && (
            <button className="ss-btn ss-btn-ghost" style={{ flex: 1 }} onClick={() => setI((n) => n - 1)} data-testid="seller-onboarding-back">Back</button>
          )}
          {last ? (
            <button className="ss-btn ss-btn-primary" style={{ flex: 2 }} onClick={finish} data-testid="seller-onboarding-start">
              Add my first item <ArrowRight size={16} />
            </button>
          ) : (
            <button className="ss-btn ss-btn-primary" style={{ flex: 2 }} onClick={() => setI((n) => n + 1)} data-testid="seller-onboarding-next">
              Next <ArrowRight size={16} />
            </button>
          )}
        </div>

        {!last && (
          <button onClick={done} className="ss-muted" style={{ background: "none", border: "none", cursor: "pointer", marginTop: 14, fontSize: 13 }} data-testid="seller-onboarding-skip-link">Skip for now</button>
        )}
      </div>
    </div>
  );
}
